<template>
  <div class="form-panel">
    <div class="form-heading">
      <div>
        <span class="eyebrow">{{ isEditing ? 'MODIFICATION' : 'NOUVELLE FICHE' }}</span>
        <h2>{{ isEditing ? 'Modifier le chauffeur' : 'Nouveau chauffeur' }}</h2>
        <p>{{ isEditing ? 'Mettez à jour les informations du chauffeur.' : 'Renseignez les informations du nouveau chauffeur.' }}</p>
      </div>
      <button class="close-btn" type="button" :disabled="busy" @click="$emit('cancel')" aria-label="Fermer le formulaire">×</button>
    </div>

    <form @submit.prevent="submit">
      <fieldset :disabled="busy">
        <div class="field">
          <label for="driver-first-name">Prénom <span>*</span></label>
          <input id="driver-first-name" v-model.trim="form.firstName" type="text" maxlength="255" autocomplete="given-name" required placeholder="Ex. : Jean" />
        </div>
        <div class="field">
          <label for="driver-last-name">Nom <span>*</span></label>
          <input id="driver-last-name" v-model.trim="form.lastName" type="text" maxlength="255" autocomplete="family-name" required placeholder="Ex. : Dupont" />
        </div>
        <div v-if="isEditing" class="field">
          <label for="driver-status">Statut</label>
          <select id="driver-status" v-model="form.status">
            <option value="ACTIVE">Actif</option>
            <option value="INACTIVE">Inactif</option>
          </select>
          <small>Le statut permet de distinguer les chauffeurs actifs et inactifs.</small>
        </div>
        <div v-if="isEditing" class="field readonly">
          <label>Dernière activité</label>
          <p>{{ driver.displayLastActivity || 'Aucune activité enregistrée' }}</p>
          <small>Cette information est mise à jour automatiquement.</small>
        </div>
      </fieldset>

      <p v-if="error" class="form-error" role="alert">{{ error }}</p>
      <p v-if="localError" class="form-error" role="alert">{{ localError }}</p>

      <div class="form-actions">
        <button type="button" class="btn-outline" :disabled="busy" @click="$emit('cancel')">Annuler</button>
        <button type="submit" class="btn-save" :disabled="busy">{{ saving ? 'Enregistrement…' : (isEditing ? 'Enregistrer' : 'Créer le chauffeur') }}</button>
      </div>
    </form>

    <DriverBadges v-if="isEditing" :driver-id="driver.id" :disabled="busy" @changed="$emit('badges-changed')" />

    <div v-if="isEditing" class="danger-zone">
      <div v-if="!confirmDelete" class="danger-row">
        <div><strong>Supprimer le chauffeur</strong><p>Les badges associés seront dissociés.</p></div>
        <button type="button" :disabled="busy" @click="confirmDelete = true">Supprimer</button>
      </div>
      <div v-else class="confirm-box">
        <strong>Confirmer la suppression ?</strong>
        <p>Cette action est définitive. Les badges associés seront conservés sans chauffeur.</p>
        <div class="confirm-actions">
          <button type="button" class="btn-outline" :disabled="busy" @click="confirmDelete = false">Conserver</button>
          <button type="button" class="btn-danger" :disabled="busy" @click="$emit('delete')">{{ deleting ? 'Suppression…' : 'Supprimer définitivement' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DriverBadges from './DriverBadges.vue';

export default {
  name: 'DriverForm',
  components: { DriverBadges },
  props: {
    driver: { type: Object, default: null },
    saving: { type: Boolean, default: false },
    deleting: { type: Boolean, default: false },
    error: { type: String, default: '' }
  },
  emits: ['save', 'delete', 'cancel', 'badges-changed'],
  data() {
    return {
      form: {
        firstName: this.driver?.firstName || '',
        lastName: this.driver?.lastName || '',
        status: this.driver?.status || 'ACTIVE'
      },
      localError: '', confirmDelete: false
    };
  },
  computed: { isEditing() { return Boolean(this.driver); }, busy() { return this.saving || this.deleting; } },
  methods: {
    submit() {
      this.localError = '';
      if (!this.form.firstName.trim() || !this.form.lastName.trim()) {
        this.localError = 'Le prénom et le nom sont obligatoires.';
        return;
      }
      const payload = { firstName: this.form.firstName.trim(), lastName: this.form.lastName.trim() };
      if (this.isEditing) payload.status = this.form.status;
      this.$emit('save', payload);
    }
  }
};
</script>

<style scoped>
.form-panel { padding: 26px; }
.form-heading { display: flex; justify-content: space-between; gap: 12px; border-bottom: 1px solid #edf0f5; padding-bottom: 23px; margin-bottom: 24px; }
.eyebrow { display: block; font-size: 10px; font-weight: 800; letter-spacing: .13em; color: #647da0; margin-bottom: 8px; }
h2 { font-size: 20px; letter-spacing: -.025em; color: #1c2c45; margin: 0 0 7px; font-weight: 750; }
.form-heading p, .danger-zone p { color: #77849a; font-size: 13px; margin: 0; line-height: 1.5; }
.close-btn { font-size: 27px; background: transparent; border: none; color: #8491a4; align-self: start; cursor: pointer; line-height: 1; }
fieldset { border: 0; padding: 0; margin: 0; min-width: 0; }
.field { margin-bottom: 20px; }
.field label { display: block; color: #33435b; font-size: 13px; font-weight: 700; margin-bottom: 9px; }
.field label span { color: #b43e42; }
.field input, .field select { display: block; width: 100%; border-radius: 9px; background: #fff; border: 1px solid #d8e1ed; min-height: 43px; padding: 10px 12px; outline: 0; color: #25354d; font-size: 14px; }
.field input:focus, .field select:focus-visible { border-color: #4479bb; box-shadow: 0 0 0 3px #e6effb; }
.field small { display: block; font-size: 11px; color: #8b98a9; line-height: 1.5; margin-top: 7px; }
.field.readonly { border: 1px solid #e8edf4; background: #f7f9fc; padding: 14px; border-radius: 10px; }
.field.readonly label { margin-bottom: 5px; }
.field.readonly p { font-size: 13px; font-weight: 700; margin: 0; color: #55677e; }
.form-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 9px; margin-top: 29px; }
.btn-outline, .btn-save, .btn-danger { border-radius: 9px; padding: 11px 14px; font-size: 13px; font-weight: 750; cursor: pointer; }
.btn-outline { border: 1px solid #d5deeb; background: #fff; color: #52647c; }
.btn-save { background: #1b4f95; border: 1px solid #1b4f95; color: white; }
.btn-save:hover { background: #153f78; }
button:disabled { opacity: .55; cursor: not-allowed; }
.form-error { font-size: 12px; color: #ad2d2d; background: #fff0f0; border-radius: 8px; padding: 11px; }
.danger-zone { margin-top: 32px; padding-top: 22px; border-top: 1px solid #edf0f5; }
.danger-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.danger-row strong, .confirm-box strong { color: #344257; font-size: 13px; }
.danger-row button { padding: 8px; border: none; background: transparent; color: #b63c3c; font-weight: 750; cursor: pointer; font-size: 12px; }
.confirm-box { background: #fff5f4; border: 1px solid #f2d6d2; border-radius: 11px; padding: 15px; }
.confirm-box p { margin: 8px 0 13px; }
.confirm-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.btn-danger { border: 1px solid #bd3d3d; background: #bd3d3d; color: #fff; }
@media (max-width: 620px) { .form-panel { padding: 20px 16px; } }
</style>
